/**
 * SourceApplicationModal — os dados de uma fonte geradora aplicada num destino (#606, E5): nível
 * de exposição, início e fim, observações, com a completude no topo. Sem `destino`, o título
 * continua o antigo ("Aplicar Fonte Geradora" / "Editar Aplicação").
 *
 * @version 2025.1.0
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  ButtonBase,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  SourceApplicationData,
  AvailableSource,
  SourceWithApplication,
  SourceExposureLevel,
  isSourceWithApplication,
  RiskTypeLabels,
} from '../../types/sourceApplication';
import { RISK_TYPE_LABELS } from '../../types/sourceGenerator';
import type { NivelDeAplicacao } from '../../types/nivelDeAplicacao';
import { hojeEmSaoPaulo } from '../../utils/hojeEmSaoPaulo';
import { BarraDeCompletude } from '../applications/BarraDeCompletude';
import { referencia } from '../risk-applications/textosDoDestino';

// ============================================================================
// TYPES
// ============================================================================

export interface SourceApplicationModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when form is submitted */
  onConfirm: (data: SourceApplicationData) => Promise<void>;
  /** The source generator being applied/edited */
  source: AvailableSource | SourceWithApplication | null;
  /** Mode: 'apply' for new application, 'edit' for existing */
  mode: 'apply' | 'edit';
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** O destino da aplicação: com ele, o título diz "Dados de <fonte> no <nível> <destino>". */
  destino?: { nivel: NivelDeAplicacao; nome: string };
  /** Onde o destino fica, abaixo do título: "MetalSP › Soldador". */
  caminho?: string;
}

interface FormValues {
  exposureLevel: SourceExposureLevel;
  startDate: Date | null;
  endDate: Date | null;
  observations: string;
}

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

/**
 * "Não pode ser no futuro", conferido **na hora de validar** e não na carga do módulo.
 *
 * `.max(new Date(), ...)` parecia a mesma coisa e não era: o esquema é montado uma vez, quando o
 * módulo carrega, então aquele `new Date()` virava um teto congelado no instante da carga da
 * página. O valor padrão do campo é o instante em que a caixa abre — sempre depois. Bastavam
 * segundos de aba aberta para o campo reprovar o próprio valor padrão.
 */
const NAO_E_FUTURO = 'nao-e-futuro';

/**
 * Início e fim são dias ("AAAA-MM-DD"), não instantes. O seletor trabalha com a meia-noite local
 * do dia; lido como instante UTC, o 10/01 gravado abriria como 09/01 em São Paulo.
 */
const dataDoDia = (dia: string): Date => {
  const [ano, mes, diaDoMes] = dia.slice(0, 10).split('-').map(Number);
  return new Date(ano, mes - 1, diaDoMes);
};
const diaDaData = (data: Date): string => format(data, 'yyyy-MM-dd');

const aindaNaoAconteceu = (valor: Date | null | undefined): boolean =>
  !valor || diaDaData(valor) <= hojeEmSaoPaulo();

const LIMITE_DAS_OBSERVACOES = 2000;

const validationSchema: yup.ObjectSchema<FormValues> = yup.object({
  exposureLevel: yup
    .mixed<SourceExposureLevel>()
    .oneOf(Object.values(SourceExposureLevel), 'Nível de exposição inválido')
    .required('Nível de exposição é obrigatório')
    .defined(),
  startDate: yup
    .date()
    .nullable()
    .defined()
    .test(NAO_E_FUTURO, 'Data de início não pode ser no futuro', aindaNaoAconteceu),
  endDate: yup
    .date()
    .nullable()
    .defined()
    .when('startDate', {
      is: (val: Date | null) => val !== null,
      then: (schema) =>
        schema.min(
          yup.ref('startDate'),
          'Data de fim deve ser posterior à data de início'
        ),
    }),
  observations: yup
    .string()
    .defined()
    .max(LIMITE_DAS_OBSERVACOES, `Observações não podem exceder ${LIMITE_DAS_OBSERVACOES} caracteres`),
});

// ============================================================================
// HELPERS
// ============================================================================

/** As quatro opções de exposição, com o que cada uma quer dizer na jornada. */
const OPCOES_DE_EXPOSICAO: { nivel: SourceExposureLevel; rotulo: string; descricao: string }[] = [
  { nivel: SourceExposureLevel.LOW, rotulo: 'Baixo', descricao: 'Exposição eventual, poucas vezes no mês.' },
  { nivel: SourceExposureLevel.MEDIUM, rotulo: 'Médio', descricao: 'Exposição durante parte da jornada.' },
  { nivel: SourceExposureLevel.HIGH, rotulo: 'Alto', descricao: 'Exposição durante a maior parte da jornada.' },
  { nivel: SourceExposureLevel.VERY_HIGH, rotulo: 'Muito alto', descricao: 'Exposição contínua, a jornada inteira.' },
];

/** O tipo de risco chega com a chave em inglês (legado) ou em português (catálogo de fontes). */
const ROTULO_DO_TIPO_DE_RISCO: Record<string, string> = { ...RiskTypeLabels, ...RISK_TYPE_LABELS };

/**
 * Incluindo, o nível pré-escolhido é o padrão, não uma escolha: o selo "confira" pede que a
 * pessoa olhe. É só dica — a completude conta o nível como preenchido, como o servidor, para a
 * janela e a listagem darem a mesma porcentagem.
 */
const nivelVeioSugerido = (mode: 'apply' | 'edit'): boolean => mode === 'apply';

// ============================================================================
// COMPONENT
// ============================================================================

export const SourceApplicationModal: React.FC<SourceApplicationModalProps> = ({
  open,
  onClose,
  onConfirm,
  source,
  mode,
  loading = false,
  error = null,
  destino,
  caminho,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      exposureLevel: SourceExposureLevel.MEDIUM,
      startDate: null,
      endDate: null,
      observations: '',
    },
  });
  /** A fonte cujo nível a pessoa conferiu nesta abertura: outra fonte começa sem conferir. */
  const [conferidaEm, setConferidaEm] = useState<AvailableSource | SourceWithApplication | null>(null);
  const conferido = !source || conferidaEm === source || !nivelVeioSugerido(mode);

  // Reset form when modal opens/closes or source changes
  useEffect(() => {
    if (open && source) {
      if (isSourceWithApplication(source)) {
        reset({
          exposureLevel: source.exposureLevel,
          startDate: source.startDate ? dataDoDia(source.startDate) : null,
          endDate: source.endDate ? dataDoDia(source.endDate) : null,
          observations: source.observations ?? '',
        });
      } else {
        reset({
          exposureLevel: SourceExposureLevel.MEDIUM,
          startDate: dataDoDia(hojeEmSaoPaulo()),
          endDate: null,
          observations: '',
        });
      }
    }
  }, [open, source, mode, reset]);

  const onSubmit = async (data: FormValues) => {
    const applicationData: SourceApplicationData = {
      exposureLevel: data.exposureLevel,
      startDate: data.startDate ? diaDaData(data.startDate) : null,
      endDate: data.endDate ? diaDaData(data.endDate) : null,
      observations: data.observations ?? null,
    };

    await onConfirm(applicationData);
  };

  const sourceName = source
    ? isSourceWithApplication(source)
      ? source.sourceName
      : source.name
    : 'Fonte Geradora';

  const riskType = source?.riskType ?? '';
  const riskTypeLabel = ROTULO_DO_TIPO_DE_RISCO[riskType] ?? riskType;
  const ocupado = loading || isSubmitting;

  const inicio = useWatch({ control, name: 'startDate' });

  // A mesma regra do servidor: nível (sempre há um) e data de início; o fim e as observações não contam.
  const faltam = inicio ? [] : ['Data de início'];
  const completude = { preenchidas: 2 - faltam.length, total: 2, faltam };

  let titulo = mode === 'apply' ? 'Aplicar Fonte Geradora' : 'Editar Aplicação';
  if (destino) titulo = `Dados de ${sourceName} ${referencia('em', destino.nivel, destino.nome)}`;

  return (
    <Dialog
      open={open}
      onClose={ocupado ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { maxWidth: 760 } }}
    >
      <DialogTitle>
        <Stack component="span" direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <span>{titulo}</span>
          {destino && riskTypeLabel && <Chip label={riskTypeLabel} size="small" variant="outlined" />}
        </Stack>
        {destino && caminho && (
          <Typography variant="body2" color="text.secondary" component="span" display="block" sx={{ mt: 0.5 }}>
            {caminho}
          </Typography>
        )}
        {!destino && (
          <Typography variant="body2" color="text.secondary" component="span" display="block" sx={{ mt: 0.5 }}>
            {sourceName}
            {riskTypeLabel && <Chip label={riskTypeLabel} size="small" sx={{ ml: 1 }} variant="outlined" />}
          </Typography>
        )}
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              <Box>
                <BarraDeCompletude completude={completude} comContagem />
                {faltam.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>{faltam.length === 1 ? 'Falta: ' : 'Faltam: '}</strong>
                    <span>{faltam.join(', ')}</span>
                  </Typography>
                )}
              </Box>

              <Controller
                name="exposureLevel"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Typography id="nivel-de-exposicao-da-fonte" variant="subtitle2" component="p" sx={{ mb: 1 }}>
                      Nível de exposição
                    </Typography>
                    <Box
                      role="radiogroup"
                      aria-labelledby="nivel-de-exposicao-da-fonte"
                      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}
                    >
                      {OPCOES_DE_EXPOSICAO.map((opcao) => {
                        const escolhida = field.value === opcao.nivel;
                        return (
                          <ButtonBase
                            key={opcao.nivel}
                            role="radio"
                            aria-checked={escolhida}
                            disabled={ocupado}
                            onClick={() => {
                              field.onChange(opcao.nivel);
                              setConferidaEm(source);
                            }}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              p: 1.5,
                              minHeight: 44,
                              borderRadius: 1,
                              border: escolhida ? 2 : 1,
                              borderColor: escolhida ? 'primary.main' : 'divider',
                              bgcolor: escolhida ? 'action.selected' : 'background.paper',
                              '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '2px' },
                            }}
                          >
                            <Typography variant="body1" fontWeight={600} component="span">
                              {opcao.rotulo}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {opcao.descricao}
                            </Typography>
                            {escolhida && !conferido && (
                              <Chip size="small" color="warning" variant="outlined" label="confira · veio sugerida" sx={{ mt: 1 }} aria-hidden />
                            )}
                          </ButtonBase>
                        );
                      })}
                    </Box>
                    {!conferido && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Este nível veio sugerido: confira e clique na opção certa, mesmo que seja a sugerida.
                      </Typography>
                    )}
                  </Box>
                )}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Data de início da exposição"
                      disabled={ocupado}
                      maxDate={dataDoDia(hojeEmSaoPaulo())}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate?.message,
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Data de fim (opcional)"
                      disabled={ocupado}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate?.message || 'Deixe vazio enquanto a exposição continua.',
                        },
                      }}
                    />
                  )}
                />
              </Stack>

              <Controller
                name="observations"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Observações"
                    multiline
                    rows={3}
                    fullWidth
                    error={!!errors.observations}
                    helperText={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <span>{errors.observations?.message ?? `Opcional · até ${LIMITE_DAS_OBSERVACOES} caracteres`}</span>
                        <span>{`${field.value?.length ?? 0} / ${LIMITE_DAS_OBSERVACOES}`}</span>
                      </Box>
                    }
                    disabled={ocupado}
                  />
                )}
              />

              <Alert severity="info">
                A fonte geradora indica <strong>de onde o risco vem</strong>; ela não aplica o risco. Para aplicar o
                risco, use <strong>Aplicação de Riscos</strong>.
              </Alert>
            </Stack>
          </LocalizationProvider>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            Salva tudo de uma vez; dá para salvar faltando informação e voltar depois.
          </Typography>
          <Button onClick={onClose} disabled={ocupado} color="inherit">
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={ocupado} startIcon={ocupado && <CircularProgress size={16} />}>
            {mode === 'apply' ? 'Aplicar' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SourceApplicationModal;
